import Image from "next/image";

import { cn } from "@/lib/utils";

export interface CustomerLogo {
  name: string;
  src: string;
  /**
   * Intrinsic width / height of the asset. Drives the reserved layout box so
   * the logo keeps its proportions when only its height is constrained.
   */
  aspectRatio: number;
  /**
   * Optical correction applied on top of the wall's row height. 1 is a plain
   * wordmark. Icon-only marks sit below 1 (a solid glyph reads heavier than a
   * wordmark set to the same height); assets that bake whitespace into their
   * viewBox sit above 1 so their ink still reaches the row height.
   */
  opticalScale?: number;
}

export const customerLogos: CustomerLogo[] = [
  { name: "Maze", src: "/company_logos/Maze.svg", aspectRatio: 5.47 },
  {
    name: "Strella",
    src: "/company_logos/strella.png",
    aspectRatio: 4.21,
    opticalScale: 0.92,
  },
  {
    name: "Ballpark",
    src: "/company_logos/Ballpark.svg",
    aspectRatio: 3.7,
    opticalScale: 1.03,
  },
  { name: "getWhys", src: "/company_logos/getwhys.svg", aspectRatio: 4.0 },
  {
    name: "Dialogue",
    src: "/company_logos/Dialogue.svg",
    aspectRatio: 4.35,
    opticalScale: 1.19,
  },
  { name: "Satellica", src: "/company_logos/Satellica.png", aspectRatio: 4.57 },
];

export const aiCustomerLogos: CustomerLogo[] = [
  {
    name: "Patronus AI",
    src: "/company_logos/PatronusAI.png",
    aspectRatio: 7.74,
  },
  // Icon-only, square, and drawn as hairline spirograph strokes at ~22% ink
  // coverage, so it carries neither width nor weight: it runs taller than the
  // wordmarks to hold the same presence beside them.
  {
    name: "Frist",
    src: "/company_logos/Frist.svg",
    aspectRatio: 1,
    opticalScale: 1.15,
  },
  {
    name: "Vetto",
    src: "/company_logos/Vetto.svg",
    aspectRatio: 3.46,
    opticalScale: 1.06,
  },
  // All-caps and heavy: its cap height fills the whole row, so it reads a size
  // larger than the mixed-case wordmarks next to it unless it sits below them.
  {
    name: "Taste Labs",
    src: "/company_logos/TasteLabs.svg",
    aspectRatio: 5.54,
    opticalScale: 0.72,
  },
];

/**
 * One logo, sized off the `--logo-h` custom property set by the surrounding
 * wall. Height is the only constrained axis and width follows the aspect
 * ratio, so every mark shares a baseline instead of each shrinking to fit its
 * own fixed-width box.
 */
export function LogoMark({
  logo,
  className,
}: {
  logo: CustomerLogo;
  className?: string;
}) {
  const scale = logo.opticalScale ?? 1;

  return (
    <Image
      src={logo.src}
      alt={`${logo.name} logo`}
      width={Math.round(100 * logo.aspectRatio)}
      height={100}
      className={cn(
        // Viewport-relative only: the height rule is what sizes these, and a
        // fixed px cap would clamp the widest lockups (Patronus AI is ~7.7:1)
        // back below the row height. This just stops them overflowing on phones.
        "w-auto max-w-[70vw] object-contain brightness-0 invert",
        className,
      )}
      style={{ height: `calc(var(--logo-h) * ${scale})` }}
    />
  );
}

interface CustomerLogosProps {
  /**
   * Text to display above the logos
   * @default "Trusted by leading research teams"
   */
  label?: string;
  /**
   * Logo entries to render. Defaults to the canonical customer-research list.
   */
  entries?: CustomerLogo[];
}

export function CustomerLogos({
  label = "Trusted by leading research teams",
  entries = customerLogos,
}: CustomerLogosProps) {
  if (entries.length === 0) {
    return null;
  }

  return (
    <div className="relative z-10 mx-auto text-center [--logo-h:26px] md:[--logo-h:32px]">
      <p className="mb-10 text-sm font-medium tracking-wider text-white/70 uppercase">
        {label}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-8 md:gap-x-14">
        {entries.map((logo) => (
          <LogoMark
            key={logo.name}
            logo={logo}
            className="opacity-80 transition-opacity duration-300 hover:opacity-100"
          />
        ))}
      </div>
    </div>
  );
}
